import React, { useState, useEffect } from 'react';

import { Box, Grid, IconButton, Typography } from '@material-ui/core';
import { Edit } from '@material-ui/icons';

import MUIDataTable, {
  MUIDataTableColumnDef,
  MUIDataTableOptions,
} from 'mui-datatables';
import { useMutation, useQuery, ApolloQueryResult } from '@apollo/client';
import PageError from '../../../../components/PageError';
import MUITableLoading from '../../../../components/MUITableLoading/MUITableLoading';
import {
  UPDATE_WEEKLY_TITLES_GOAL,
  GET_WEEKLY_TITLES_GOAL,
} from '../../../../queries/dashboard';
import EditWeeklyTitlesGoalModal from '../EditWeeklyTitlesGoalModal/EditWeeklyTitlesGoalModal';
import useStyles from './styles';
import { UserService } from '../../../../services/User/UserService';

interface IWeeklyTitlesGoalProps {
  updatePendingTitles: (
    variables?:
      | Partial<{
          sites: string[];
          dateStart: Date;
          dateEnd: Date;
        }>
      | undefined,
  ) => Promise<ApolloQueryResult<any>>;
}

export const WeeklyTitlesGoal: React.FC<IWeeklyTitlesGoalProps> = ({
  updatePendingTitles,
}) => {
  const classes = useStyles();
  const sites = UserService.useSitesFromAccess();

  const [data, setData] = useState<IWeeklyTitlesGoal>();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedEditSite, setSelectedEditSite] = useState<
    IWeeklyTitlesGoalBySite | undefined
  >();

  const { error, data: rawData, loading, refetch: refetchGoal } = useQuery(
    GET_WEEKLY_TITLES_GOAL,
    {
      variables: {
        sites,
      },
      fetchPolicy: 'network-only',
    },
  );

  useEffect(() => {
    if (rawData) {
      setData(rawData.weeklyTitlesGoal);
    }
  }, [rawData]);

  const [updateWeeklyTitlesGoal] = useMutation(UPDATE_WEEKLY_TITLES_GOAL);

  const enterEditMode = (weeklyTitle: IWeeklyTitlesGoalBySite) => {
    console.log(weeklyTitle);
    setEditModalOpen(true);
    setSelectedEditSite(weeklyTitle);
  };

  const handleEditWeeklyTitlesGoalClose = () => {
    setEditModalOpen(false);
  };

  const onSaveWeekTitlesGoal = async (
    sitePreferencesId: string,
    articles: number,
    news: number,
    translator: number,
  ) => {
    await updateWeeklyTitlesGoal({
      variables: {
        sitePreferencesId,
        articles,
        news,
        translator,
      },
    });

    setEditModalOpen(false);

    refetchGoal();
    updatePendingTitles!();
  };

  const columns: MUIDataTableColumnDef[] = [
    {
      name: 'domain',
      label: 'Site',
      options: {
        filter: false,
        sort: true,
        customBodyRender: function ActionsCell(value: any) {
          return <b>{value}</b>;
        },
      },
    },
    {
      name: 'article',
      label: 'Article (AR)',
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: 'news',
      label: 'News (NW)',
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: 'translator',
      label: 'Translator (TL)',
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: 'other',
      label: 'Others',
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: 'domain',
      label: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: function ActionsCell(value: any) {
          const weeklyTitles = data?.bySite?.find(
            (site) => site.domain === value,
          );
          return (
            <IconButton
              onClick={() => weeklyTitles && enterEditMode(weeklyTitles)}
              style={{ padding: 2 }}
            >
              <Edit />
            </IconButton>
          );
        },
      },
    },
  ];

  const options: MUIDataTableOptions = {
    selectableRowsOnClick: false,
    selectableRowsHeader: false,
    selectableRows: 'none',
    responsive: 'standard',
    sort: true,
    filter: false,
    pagination: true,
    serverSide: false,
    search: false,
    download: false,
    print: false,
    viewColumns: false,
    customToolbar: undefined,
    rowsPerPage: 10,
    rowsPerPageOptions: [5, 10, 25, 50],
  };

  if (error) return <PageError />;

  return (
    <Grid item xs={12} className={classes.container}>
      <div
        style={{
          boxShadow:
            '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
          backgroundColor: 'white',
        }}
        className={classes.gridItem}
      >
        <Typography
          className={classes.title}
          variant="h4"
          id="tableTitle"
          style={{ paddingTop: 16 }}
        >
          Week Titles Goal
        </Typography>
        <Typography>
          <Grid container className={classes.totalContainer}>
            <Grid item className={classes.totalTextContainer}>
              <Box textAlign="center" fontWeight={600}>
                Total
              </Box>
              <Box textAlign="center">
                {loading ? '-' : data?.total?.total ?? '-'}
              </Box>
            </Grid>
            <Grid item className={classes.totalTextContainer}>
              <Box textAlign="center" fontWeight={600}>
                Article
              </Box>
              <Box textAlign="center">
                {loading ? '-' : data?.total?.article ?? '-'}
              </Box>
            </Grid>
            <Grid item className={classes.totalTextContainer}>
              <Box textAlign="center" fontWeight={600}>
                News
              </Box>
              <Box textAlign="center">
                {loading ? '-' : data?.total?.news ?? '-'}
              </Box>
            </Grid>
            <Grid item className={classes.totalTextContainer}>
              <Box textAlign="center" fontWeight={600}>
                Translator
              </Box>
              <Box textAlign="center">
                {loading ? '-' : data?.total?.translator ?? '-'}
              </Box>
            </Grid>
            <Grid item className={classes.totalTextContainer}>
              <Box textAlign="center" fontWeight={600}>
                Other
              </Box>
              <Box textAlign="center">
                {loading ? '-' : data?.total?.other ?? '-'}
              </Box>
            </Grid>
          </Grid>
        </Typography>

        {loading ? (
          <MUITableLoading
            withRefresh={false}
            hasFilter={false}
            selectable="none"
            columns={[
              'Site',
              'Article (AR)',
              'News (NW)',
              'Translator (TL)',
              'Others',
              'Actions',
            ]}
          />
        ) : (
          <MUIDataTable
            title=""
            data={data?.bySite || []}
            columns={columns}
            options={options}
          />
        )}
      </div>
      <EditWeeklyTitlesGoalModal
        open={editModalOpen}
        editingData={selectedEditSite}
        handleClose={handleEditWeeklyTitlesGoalClose}
        onSaveWeekTitlesGoal={onSaveWeekTitlesGoal}
      />
    </Grid>
  );
};
